import React, { useMemo } from 'react';
import {
  FormDialog,
  IMAGE_FILE_ACCEPT,
  RhfFileInput,
  RhfTextField,
  toast,
} from '@core/components';
import type { StoleCut } from '../../api/stoleCut';
import { createStoleCut, updateStoleCut } from '../../api/stoleCut';
import type { Vestment } from '../../api/vestment';
import { getImageThumbnailUrl } from '../utils/image';

interface StoleCutDialogProps {
  open: boolean;
  onClose: () => void;
  vestment: Vestment;
  cut?: StoleCut | null;
  onSuccess?: () => void;
}

type StoleCutFormValues = {
  name: string;
  image: File | null;
};

const StoleCutDialog: React.FC<StoleCutDialogProps> = ({
  open,
  onClose,
  vestment,
  cut,
  onSuccess,
}) => {
  const defaultValues = useMemo<StoleCutFormValues>(
    () => ({
      name: cut?.name ?? '',
      image: null,
    }),
    [cut]
  );

  const onSubmit = async (data: StoleCutFormValues) => {
    const name = data.name.trim();
    try {
      if (cut) {
        await updateStoleCut(cut.id, {
          name,
          image: data.image ?? undefined,
        });
        toast.success('Type updated');
      } else {
        await createStoleCut({
          vestment: vestment.id,
          name,
          image: data.image ?? undefined,
        });
        toast.success(`"${name}" added`);
      }
      onSuccess?.();
    } catch (error) {
      toast.apiError('Failed to save type.', error);
      throw error;
    }
  };

  return (
    <FormDialog<StoleCutFormValues>
      open={open}
      onClose={onClose}
      title={cut ? `Edit ${vestment.name} Type` : `Add ${vestment.name} Type`}
      defaultValues={defaultValues}
      onSubmit={onSubmit}
      submitText={cut ? 'Update' : 'Add'}
      maxWidth="xs"
      addAnother={!cut}
    >
      <RhfTextField<StoleCutFormValues> name="name" label="Name" required />
      <RhfFileInput<StoleCutFormValues>
        name="image"
        label="Image"
        accept={IMAGE_FILE_ACCEPT}
        initialPreviewSrc={getImageThumbnailUrl(cut)}
      />
    </FormDialog>
  );
};

export default StoleCutDialog;
